import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, Save, RefreshCw, Crown, Timer, ChevronDown, ChevronRight, Bot, Swords,
} from "lucide-react";
import { toast } from "sonner";
import TranslatedInput from "@/components/admin/TranslatedInput";

/**
 * Admin · Eliminatórias (Desafios)
 *
 * Lists all eliminatórias grouped by game (only games with `status="eliminatorias"`).
 * Lets the admin create / edit / reorder / delete each challenge.
 */
export default function AdminEliminatorias() {
  const [games, setGames] = useState([]);
  const [items, setItems] = useState([]);
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [newRow, setNewRow] = useState({}); // {[gameId]: {name, max_attempts, target_time, mode, target_robot_id}}
  const [creating, setCreating] = useState({}); // {[gameId]: bool}

  const load = async () => {
    setLoading(true);
    try {
      const [gRes, eRes, rRes] = await Promise.all([
        api.get("/games"),
        api.get("/eliminatorias"),
        api.get("/robots"),
      ]);
      const elimGames = (gRes.data || []).filter((g) => g.status === "eliminatorias");
      setGames(elimGames);
      setItems(eRes.data || []);
      setRobots((rRes.data || []).filter((r) => r.active !== false));
      // Auto-expand all games on first load.
      setExpanded((cur) => {
        if (Object.keys(cur).length) return cur;
        const map = {};
        elimGames.forEach((g) => { map[g.id] = true; });
        return map;
      });
    } catch (e) {
      toast.error("Erro a carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const byGame = useMemo(() => {
    const map = new Map();
    games.forEach((g) => map.set(g.id, []));
    items.forEach((e) => {
      if (!map.has(e.game_id)) map.set(e.game_id, []);
      map.get(e.game_id).push(e);
    });
    // Sort each group by order then name.
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return map;
  }, [games, items]);

  const updateRow = async (id, patch) => {
    try {
      await api.put(`/eliminatorias/${id}`, patch);
      toast.success("Atualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    }
  };

  const removeRow = async (id, label) => {
    if (!window.confirm(`Apagar a eliminatória "${label}"?\nAs tentativas associadas serão também apagadas.`)) return;
    try {
      await api.delete(`/eliminatorias/${id}`);
      toast.success("Apagada");
      load();
    } catch (e) {
      toast.error("Erro a apagar");
    }
  };

  const createForGame = async (gameId) => {
    const draft = newRow[gameId] || {};
    if (!draft.name || !draft.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    const mode = draft.mode || "target_time";
    if (mode === "vencer_robot" && !draft.target_robot_id) {
      toast.error("Escolhe o robot a derrotar");
      return;
    }
    if (mode === "wins_vs_losses") {
      const total = Number(draft.total_games) || 5;
      const wins  = Number(draft.wins_required) || 3;
      if (total < 1 || wins < 1) {
        toast.error("Total de jogos e vitórias necessárias têm de ser >= 1");
        return;
      }
      if (wins > total) {
        toast.error("Vitórias necessárias não pode exceder o total de jogos");
        return;
      }
    }
    if (mode === "avg_time") {
      const total = Number(draft.total_games) || 5;
      const maxA  = Number(draft.max_attempts) || 3;
      const avgT  = Number(draft.target_time) || 60;
      if (total < 1) {
        toast.error("N.º de jogos por tentativa tem de ser >= 1");
        return;
      }
      if (maxA < 1) {
        toast.error("N.º de tentativas tem de ser >= 1");
        return;
      }
      if (avgT <= 0) {
        toast.error("Tempo médio alvo tem de ser > 0 s");
        return;
      }
    }
    setCreating((c) => ({ ...c, [gameId]: true }));
    try {
      const order = (byGame.get(gameId) || []).length;
      const payload = {
        game_id: gameId,
        name: draft.name.trim(),
        max_attempts: Number(draft.max_attempts) || 3,
        target_time: Number(draft.target_time) || 60,
        mode,
        target_robot_id: mode === "vencer_robot" ? draft.target_robot_id : null,
        total_games: Number(draft.total_games) || 5,
        wins_required: Number(draft.wins_required) || 3,
        order,
        active: true,
      };
      await api.post("/eliminatorias", payload);
      toast.success("Eliminatória criada");
      setNewRow((p) => ({ ...p, [gameId]: { name: "", max_attempts: 3, target_time: 60, mode: "target_time", target_robot_id: "", total_games: 5, wins_required: 3 } }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a criar");
    } finally {
      setCreating((c) => ({ ...c, [gameId]: false }));
    }
  };

  // Robots compatible with a given game (no game restriction OR includes this game id).
  const robotsForGame = useMemo(() => {
    const cache = new Map();
    games.forEach((g) => {
      const list = robots.filter((r) => {
        const gids = r.game_ids || [];
        if (gids.length) return gids.includes(g.id);
        if (r.game_id) return r.game_id === g.id;
        return true; // robot has no game restriction → available everywhere
      });
      cache.set(g.id, list);
    });
    return cache;
  }, [games, robots]);

  return (
    <div data-testid="admin-elim-challenges">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-violet-600 text-white p-2.5 shadow-md">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900 leading-tight">Eliminatórias · Desafios</h1>
            <p className="text-sm text-slate-500">Cria as rondas de eliminatória de cada jogo (target time, tentativas, ordem).</p>
          </div>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full" data-testid="elim-ch-reload">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Recarregar
        </Button>
      </div>

      {!loading && !games.length && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 text-amber-900" data-testid="elim-ch-no-games">
          <strong>Sem jogos em modo Eliminatórias.</strong>
          <div className="text-sm mt-1">Vai a <code className="bg-white px-1.5 py-0.5 rounded border border-amber-200">Admin → Jogos</code>, edita um jogo e muda o <em>Status</em> para <b>Eliminatórias</b>. Depois volta aqui para criar os desafios.</div>
        </div>
      )}

      <div className="space-y-4">
        {games.map((g) => {
          const list = byGame.get(g.id) || [];
          const open = !!expanded[g.id];
          const draft = newRow[g.id] || { name: "", max_attempts: 3, target_time: 60, mode: "target_time", target_robot_id: "", total_games: 5, wins_required: 3 };
          const compatibleRobots = robotsForGame.get(g.id) || [];
          return (
            <div key={g.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`elim-ch-game-${g.id}`}>
              <button
                onClick={() => setExpanded((e) => ({ ...e, [g.id]: !e[g.id] }))}
                className="w-full px-5 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white flex items-center justify-between hover:opacity-95"
                data-testid={`elim-ch-toggle-${g.id}`}
              >
                <div className="flex items-center gap-3">
                  {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-violet-200 font-bold">Jogo</div>
                    <div className="font-[Fredoka] font-bold">{g.name}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`elim-ch-game-inherits-${g.id}`}>
                      <ModeBadge mode={g.mode} />
                      {g.mode === "sorte_ajustada" && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-300/95 text-amber-950 text-[10px] font-bold uppercase tracking-wide"
                          title="Máx. superletras repetidas"
                          data-testid={`elim-ch-max-repeats-${g.id}`}
                        >
                          Máx repetições: {g.max_repeated_letters ?? 0}
                        </span>
                      )}
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 border border-white/30 text-[10px] font-bold uppercase tracking-wide"
                        title="SUPERT a completar"
                      >
                        {g.supert_count ?? 1}× SUPERT
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-xs bg-white/15 rounded-full px-3 py-1">
                  {list.length} {list.length === 1 ? "eliminatória" : "eliminatórias"}
                </div>
              </button>

              {open && (
                <div className="p-4">
                  {list.length > 0 && (
                    <table className="w-full text-sm mb-4">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600 w-16">Ordem</th>
                          <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600">Nome</th>
                          <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600 w-36">Modo</th>
                          <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600 w-44">Tempo / Robot / Vit.·Jogos</th>
                          <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600 w-28">Tentativas</th>
                          <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-600 w-20">Ativa</th>
                          <th className="text-right px-3 py-2 text-xs font-bold uppercase text-slate-600 w-20">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((e) => (
                          <Row
                            key={e.id}
                            item={e}
                            robots={compatibleRobots}
                            onSave={updateRow}
                            onDelete={removeRow}
                          />
                        ))}
                      </tbody>
                    </table>
                  )}
                  {!list.length && (
                    <div className="text-sm text-slate-500 italic mb-4" data-testid={`elim-ch-empty-${g.id}`}>
                      Ainda não há eliminatórias para este jogo. Cria a primeira abaixo.
                    </div>
                  )}

                  <div className="grid md:grid-cols-12 gap-3 items-end bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <div className="md:col-span-4">
                      <Label>Nome da nova eliminatória</Label>
                      <Input
                        data-testid={`elim-ch-new-name-${g.id}`}
                        value={draft.name}
                        onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, name: e.target.value } }))}
                        placeholder="ex: Eliminatória 1 · Distrito"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <Label>Modo</Label>
                      <Select
                        value={draft.mode || "target_time"}
                        onValueChange={(v) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, mode: v } }))}
                      >
                        <SelectTrigger data-testid={`elim-ch-new-mode-${g.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="target_time" data-testid={`elim-ch-new-mode-${g.id}-target_time`}>
                            <span className="inline-flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> Tempo alvo</span>
                          </SelectItem>
                          <SelectItem value="vencer_robot" data-testid={`elim-ch-new-mode-${g.id}-vencer_robot`}>
                            <span className="inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> Vencer Robot</span>
                          </SelectItem>
                          <SelectItem value="wins_vs_losses" data-testid={`elim-ch-new-mode-${g.id}-wins_vs_losses`}>
                            <span className="inline-flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Vitórias vs Derrotas</span>
                          </SelectItem>
                          <SelectItem value="avg_time" data-testid={`elim-ch-new-mode-${g.id}-avg_time`}>
                            <span className="inline-flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> Tempo médio</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(() => {
                      const mode = draft.mode || "target_time";
                      if (mode === "target_time") return (
                        <div className="md:col-span-2">
                          <Label className="flex items-center gap-1"><Timer className="w-3.5 h-3.5" /> Tempo-alvo (s)</Label>
                          <Input
                            data-testid={`elim-ch-new-time-${g.id}`}
                            type="number"
                            min={5}
                            max={3600}
                            value={draft.target_time}
                            onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, target_time: e.target.value } }))}
                          />
                        </div>
                      );
                      if (mode === "vencer_robot") return (
                        <div className="md:col-span-2">
                          <Label className="flex items-center gap-1"><Bot className="w-3.5 h-3.5" /> Robot alvo</Label>
                          <Select
                            value={draft.target_robot_id || ""}
                            onValueChange={(v) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, target_robot_id: v } }))}
                          >
                            <SelectTrigger data-testid={`elim-ch-new-robot-${g.id}`}>
                              <SelectValue placeholder={compatibleRobots.length ? "Escolher…" : "Sem robots"} />
                            </SelectTrigger>
                            <SelectContent>
                              {compatibleRobots.map((r) => (
                                <SelectItem key={r.id} value={r.id} data-testid={`elim-ch-new-robot-${g.id}-${r.id}`}>
                                  {r.name} {r.alcunha ? `· "${r.alcunha}"` : ""} ({r.mental_strength}/10)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                      // avg_time — two mini fields inline (Jogos / Média).
                      // NOTE: the number of full attempts is edited on the
                      // dedicated "TENTATIVAS" column so we don't duplicate
                      // that input here.
                      if (mode === "avg_time") return (
                        <div className="md:col-span-2 grid grid-cols-2 gap-1">
                          <div>
                            <Label className="text-[10px] flex items-center gap-0.5"><Timer className="w-3 h-3" /> Jogos</Label>
                            <Input
                              data-testid={`elim-ch-new-avg-total-${g.id}`}
                              type="number"
                              min={1}
                              max={30}
                              value={draft.total_games ?? 5}
                              onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, total_games: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Média (s)</Label>
                            <Input
                              data-testid={`elim-ch-new-avg-target-${g.id}`}
                              type="number"
                              min={1}
                              max={3600}
                              value={draft.target_time ?? 60}
                              onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, target_time: e.target.value } }))}
                            />
                          </div>
                        </div>
                      );
                      return (
                        <div className="md:col-span-2 grid grid-cols-2 gap-1">
                          <div>
                            <Label className="text-[10px] flex items-center gap-0.5"><Swords className="w-3 h-3" /> Jogos</Label>
                            <Input
                              data-testid={`elim-ch-new-total-${g.id}`}
                              type="number"
                              min={1}
                              max={30}
                              value={draft.total_games ?? 5}
                              onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, total_games: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px]">Vit. p/ passar</Label>
                            <Input
                              data-testid={`elim-ch-new-wins-${g.id}`}
                              type="number"
                              min={1}
                              max={30}
                              value={draft.wins_required ?? 3}
                              onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, wins_required: e.target.value } }))}
                            />
                          </div>
                        </div>
                      );
                    })()}
                    <div className="md:col-span-1">
                      <Label>Tentativas</Label>
                      <Input
                        data-testid={`elim-ch-new-attempts-${g.id}`}
                        type="number"
                        min={1}
                        max={10}
                        value={draft.max_attempts}
                        onChange={(e) => setNewRow((p) => ({ ...p, [g.id]: { ...draft, max_attempts: e.target.value } }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Button
                        onClick={() => createForGame(g.id)}
                        disabled={creating[g.id]}
                        className="w-full bg-violet-600 hover:bg-violet-700 rounded-full"
                        data-testid={`elim-ch-create-${g.id}`}
                      >
                        <Plus className="w-4 h-4 mr-1.5" /> {creating[g.id] ? "A criar…" : "Adicionar"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ item, robots = [], onSave, onDelete }) {
  const [edit, setEdit] = useState({
    order: item.order ?? 0,
    name: item.name,
    max_attempts: item.max_attempts,
    target_time: item.target_time,
    mode: item.mode || "target_time",
    target_robot_id: item.target_robot_id || "",
    total_games: item.total_games ?? 5,
    wins_required: item.wins_required ?? 3,
    active: item.active !== false,
    translations: item.translations || {},
  });
  const initialMode = item.mode || "target_time";
  const initialRobot = item.target_robot_id || "";
  const dirty =
    edit.order !== (item.order ?? 0) ||
    edit.name !== item.name ||
    Number(edit.max_attempts) !== item.max_attempts ||
    Number(edit.target_time) !== item.target_time ||
    edit.mode !== initialMode ||
    edit.target_robot_id !== initialRobot ||
    Number(edit.total_games) !== (item.total_games ?? 5) ||
    Number(edit.wins_required) !== (item.wins_required ?? 3) ||
    edit.active !== (item.active !== false) ||
    JSON.stringify(edit.translations || {}) !== JSON.stringify(item.translations || {});

  const robotName = useMemo(() => {
    if (edit.mode !== "vencer_robot") return null;
    const r = robots.find((x) => x.id === edit.target_robot_id);
    return r ? `${r.name}${r.alcunha ? ` · "${r.alcunha}"` : ""}` : null;
  }, [edit.mode, edit.target_robot_id, robots]);

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`elim-ch-row-${item.id}`}>
      <td className="px-3 py-2">
        <Input
          type="number"
          value={edit.order}
          onChange={(e) => setEdit((p) => ({ ...p, order: Number(e.target.value) }))}
          className="w-16"
          data-testid={`elim-ch-order-${item.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <TranslatedInput
          compact
          field="name"
          value={edit.name}
          translations={edit.translations}
          onChange={({ value, translations }) => setEdit((p) => ({ ...p, name: value, translations }))}
          testid={`elim-ch-name-${item.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Select
          value={edit.mode}
          onValueChange={(v) => setEdit((p) => ({ ...p, mode: v }))}
        >
          <SelectTrigger data-testid={`elim-ch-mode-${item.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="target_time" data-testid={`elim-ch-mode-${item.id}-target_time`}>
              <span className="inline-flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> Tempo alvo</span>
            </SelectItem>
            <SelectItem value="vencer_robot" data-testid={`elim-ch-mode-${item.id}-vencer_robot`}>
              <span className="inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> Vencer Robot</span>
            </SelectItem>
            <SelectItem value="wins_vs_losses" data-testid={`elim-ch-mode-${item.id}-wins_vs_losses`}>
              <span className="inline-flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Vitórias vs Derrotas</span>
            </SelectItem>
            <SelectItem value="avg_time" data-testid={`elim-ch-mode-${item.id}-avg_time`}>
              <span className="inline-flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> Tempo médio</span>
            </SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2">
        {edit.mode === "target_time" ? (
          <Input
            type="number"
            min={5}
            max={3600}
            value={edit.target_time}
            onChange={(e) => setEdit((p) => ({ ...p, target_time: e.target.value }))}
            className="w-28"
            data-testid={`elim-ch-time-${item.id}`}
          />
        ) : edit.mode === "vencer_robot" ? (
          <Select
            value={edit.target_robot_id || ""}
            onValueChange={(v) => setEdit((p) => ({ ...p, target_robot_id: v }))}
          >
            <SelectTrigger data-testid={`elim-ch-robot-${item.id}`}>
              <SelectValue placeholder={robots.length ? "Escolher…" : "Sem robots"}>
                {robotName || "Escolher…"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {robots.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`elim-ch-robot-${item.id}-${r.id}`}>
                  {r.name} {r.alcunha ? `· "${r.alcunha}"` : ""} ({r.mental_strength}/10)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : edit.mode === "avg_time" ? (
          // avg_time — two mini fields (Jogos / Média). The number of full
          // attempts is edited on the dedicated "TENTATIVAS" column so we
          // don't duplicate that input inline here.
          <div className="flex items-center gap-1">
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 leading-none">Jogos</span>
              <Input
                type="number"
                min={1}
                max={30}
                value={edit.total_games}
                onChange={(e) => setEdit((p) => ({ ...p, total_games: e.target.value }))}
                className="w-16"
                data-testid={`elim-ch-avg-total-${item.id}`}
              />
            </div>
            <span className="text-slate-400 text-xs mt-3">/</span>
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 leading-none">Média (s)</span>
              <Input
                type="number"
                min={1}
                max={3600}
                value={edit.target_time}
                onChange={(e) => setEdit((p) => ({ ...p, target_time: e.target.value }))}
                className="w-16"
                data-testid={`elim-ch-avg-target-${item.id}`}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 leading-none">Jogos</span>
              <Input
                type="number"
                min={1}
                max={30}
                value={edit.total_games}
                onChange={(e) => setEdit((p) => ({ ...p, total_games: e.target.value }))}
                className="w-16"
                data-testid={`elim-ch-total-${item.id}`}
              />
            </div>
            <span className="text-slate-400 text-xs mt-3">/</span>
            <div className="flex flex-col">
              <span className="text-[9px] text-slate-500 leading-none">Vit.</span>
              <Input
                type="number"
                min={1}
                max={30}
                value={edit.wins_required}
                onChange={(e) => setEdit((p) => ({ ...p, wins_required: e.target.value }))}
                className="w-16"
                data-testid={`elim-ch-wins-${item.id}`}
              />
            </div>
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          min={1}
          max={10}
          value={edit.max_attempts}
          onChange={(e) => setEdit((p) => ({ ...p, max_attempts: e.target.value }))}
          className="w-20"
          data-testid={`elim-ch-attempts-${item.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Switch
          checked={edit.active}
          onCheckedChange={(v) => setEdit((p) => ({ ...p, active: v }))}
          data-testid={`elim-ch-active-${item.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onSave(item.id, {
              name: edit.name,
              order: Number(edit.order) || 0,
              max_attempts: Number(edit.max_attempts) || 3,
              target_time: Number(edit.target_time) || 60,
              mode: edit.mode,
              target_robot_id: edit.mode === "vencer_robot" ? (edit.target_robot_id || null) : null,
              total_games: Number(edit.total_games) || 5,
              wins_required: Number(edit.wins_required) || 3,
              active: edit.active,
              translations: edit.translations || {},
            })}
            disabled={!dirty}
            className="text-emerald-600 hover:text-emerald-700 disabled:opacity-30 p-1"
            data-testid={`elim-ch-save-${item.id}`}
            title="Guardar"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(item.id, item.name)}
            className="text-rose-600 hover:text-rose-700 p-1"
            data-testid={`elim-ch-delete-${item.id}`}
            title="Apagar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

const MODE_META = {
  normal: { label: "Normal", cls: "bg-emerald-300/95 text-emerald-950" },
  sem_sorte: { label: "Sem sorte", cls: "bg-sky-300/95 text-sky-950" },
  sorte_ajustada: { label: "Sorte ajustada", cls: "bg-amber-300/95 text-amber-950" },
};

function ModeBadge({ mode }) {
  const meta = MODE_META[mode] || MODE_META.normal;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${meta.cls}`}
      title="Modo de jogo herdado da configuração do jogo"
      data-testid={`elim-ch-mode-badge-${mode}`}
    >
      Modo: {meta.label}
    </span>
  );
}

